import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { budapestDay, nowTimeLabel, periodHoldsNow } from '../../../Utils/nowMarker';
import NowLine, { NOW_LINE_DASH } from '../../../Components/NowLine';
import { totalPassengers } from '../../Portal/portalFormat';
import { NOW_LABEL_EN, plural, routeLabel } from '../../Portal/Operator/operatorFormat';
import {
  CHIPS, HOUR_PX, LOOK_LABELS, ROW_PX, axisFor, barGeometry, barLook, buildLanes, driverOverlaps,
  hourTicks, laneSummary, packRows, personName, tripClock, tripWindow, xOf,
} from '../dayTimeline';
import TripPanel from './TripPanel';

/** A bar at least this wide has room for its route and driver. */
const WIDE_BAR_PX = 190;

/** The looks a bar carries a warning triangle for: the office should act. */
const ALERT_LOOKS = ['late', 'waiting', 'no-show'];

/** The legend, in the order a day runs. `swatch` is the look it explains. */
const LEGEND = ['done', 'live', 'upcoming', 'late', 'waiting', 'no-show', 'off-app', 'unassigned'];

// eslint-disable-next-line no-underscore-dangle
const idOf = (trip) => trip._id;

const surname = (name) => String(name || '').trim().split(/\s+/).pop() || '—';

/** 'Thompson ×4' */
const whoOf = (trip) => {
  const pax = totalPassengers(trip);
  return pax ? `${surname(trip.full_name)} ×${pax}` : surname(trip.full_name);
};

const routeOf = (trip) => routeLabel({ from: trip.destination_from, to: trip.destination_to });

/**
 * Who is driving this bar's trip for this lane, in a word or two. An
 * experience needs no driver (`needsDriver`), so one without is named by
 * who runs it.
 */
const driverOf = ({ trip, entry }) => {
  if (!entry) return 'no operator';
  if (personName(entry.driver)) return personName(entry.driver);
  if (entry.role === 'guide') return 'guide';
  return trip.booking_type === 'experience' ? entry.operator?.name || '' : 'no driver named';
};

/**
 * The bar's whole story in one sentence, for its `title` and its
 * accessible name: '14:00 Elena Garcia, 2 pax, BUD T2B to Gresham Palace,
 * flight W6 2203, Attila Nagy — driver waiting, until about 14:35'.
 */
const sentenceOf = (bar, look, overlapping) => {
  const { trip, leg: { start, end, isReturn } } = bar;
  const pax = totalPassengers(trip);

  const facts = [
    `${tripClock(trip, start)} ${trip.full_name || 'No name'}`,
    isReturn ? 'return leg' : '',
    pax ? `${pax} pax` : '',
    routeOf(trip).replace(' → ', ' to '),
    trip.flight_number ? `flight ${trip.flight_number}` : '',
    driverOf(bar),
  ].filter(Boolean).join(', ');
  const ending = end === null ? 'end not estimated' : `until about ${tripClock(trip, end)}`;
  const clash = overlapping ? ', the same driver has another trip at this time' : '';

  return `${facts} — ${LOOK_LABELS[look].toLowerCase()}, ${ending}${clash}`;
};

const iconOf = (trip, alert) => {
  if (alert) return 'fa-exclamation-triangle';
  if (trip.booking_type === 'experience') return 'fa-compass';
  return trip.flight_number ? 'fa-plane' : '';
};

/**
 * THE OFFICE'S DAY, as one lane per operator.
 *
 * Each trip is a bar from its pickup to the API's estimated end, with a
 * faint tail to its late end; the colour is the dispatch board's reading of
 * the trip (`barLook`). The red line is where the clock has got to — only on
 * a day that contains this moment (`periodHoldsNow`), placed on the Budapest
 * clock. Clicking a bar opens the booking beside the timeline.
 *
 * ONE clock per render, exactly as before: every "is this late" on the
 * screen — the colours, the chip counts and the panel — is answered against
 * the same instant. It moves when the page does: Refresh, the filters, and
 * the read after a booking is edited.
 */
function DayTimeline({
  trips,
  isLoading,
  total,
  dayFrom,
  dayTo,
  onEditBooking,
  onAssignOperator,
  onAssignDriver,
  onViewDetails,
  onViewDriverEvents,
  onSearchLinkedBookings,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState('all');
  const scrollRef = useRef(null);
  const scrolledFor = useRef(null);

  const now = new Date();
  const day = dayFrom ? budapestDay(dayFrom) : '';
  const legOf = (trip) => tripWindow(trip, day);
  const timed = trips.filter((trip) => legOf(trip).start !== null);
  const untimed = trips.filter((trip) => legOf(trip).start === null);
  const axis = axisFor(timed.map(legOf), day);
  const overlaps = driverOverlaps(timed, day);
  const overlapping = new Set(overlaps.flatMap(({ a, b }) => [a, b]));
  const context = { now, day, overlapping };
  const chip = CHIPS.find(({ key }) => key === filter);
  const lanes = buildLanes(timed, day);
  const trackWidth = axis ? xOf(axis, axis.to) : 0;
  const showsNow = Boolean(axis) && periodHoldsNow(dayFrom, dayTo, now)
    && now.getTime() >= axis.from && now.getTime() < axis.to;
  const nowX = showsNow ? xOf(axis, now.getTime()) : 0;

  // On a fresh day's trips, scroll so the last three hours are in view —
  // once per day, never on a refresh the office is reading through.
  useEffect(() => {
    if (!scrollRef.current || scrolledFor.current === day) return;
    scrolledFor.current = day;
    scrollRef.current.scrollLeft = showsNow ? Math.max(0, nowX - 3 * HOUR_PX) : 0;
  }, [trips]);

  if (isLoading && !trips.length) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading trips...</p>
      </div>
    );
  }

  if (!trips.length) {
    return (
      <div className="dov-empty">
        <i className="fa fa-calendar-times" />
        <p>No trips found for this date.</p>
      </div>
    );
  }

  const selected = trips.find((trip) => idOf(trip) === selectedId);
  const select = (trip) => setSelectedId(idOf(trip));
  const faded = (trip) => !chip.test(trip, context);
  const pax = trips.reduce((sum, trip) => sum + totalPassengers(trip), 0);
  const operators = lanes.filter((lane) => !lane.unassigned).length;

  return (
    <div className={`dov${isLoading ? ' dov--loading' : ''}`} style={{ '--dov-hour': `${HOUR_PX}px` }}>
      <div className="dov-filters">
        <div role="group" aria-label="Show" className="dov-chips">
          {CHIPS.map((option) => {
            const count = trips.filter((trip) => option.test(trip, context)).length;
            const on = filter === option.key;
            return (
              <button
                key={option.key}
                type="button"
                className={`dov-chip${on ? ' is-on' : ''}${option.hot && count ? ' is-hot' : ''}`}
                aria-pressed={on}
                onClick={() => setFilter(on ? 'all' : option.key)}
              >
                {option.label}
                <span className="dov-chip__count">{count}</span>
              </button>
            );
          })}
        </div>
        <span className="dov-summary">
          {[
            plural(trips.length, 'trip', 'trips'),
            `${pax} pax`,
            plural(operators, 'operator', 'operators'),
            total > trips.length ? `showing ${trips.length} of ${total}` : '',
          ].filter(Boolean).join(' · ')}
        </span>
      </div>

      <ul className="dov-legend" aria-label="Legend">
        {LEGEND.map((look) => (
          <li key={look}>
            <span className={`dov-swatch dov-trip--${look}`} />
            {LOOK_LABELS[look]}
          </li>
        ))}
        <li>
          <span className="dov-swatch dov-swatch--overlap" />
          Same driver twice
        </li>
        <li>
          <span className="dov-swatch dov-trip--upcoming dov-trip--open-end" />
          End not estimated
        </li>
        <li className="dov-legend__note">Transfer length is an estimate; tours use their programme hours.</li>
      </ul>

      <div className="dov-body">
        <section className="dov-board" aria-label="Trips by operator">
          {axis && (
            <div className="dov-scroll" ref={scrollRef}>
              <div className="dov-grid">
                <div className="dov-row dov-row--head">
                  <div className="dov-label">Operator</div>
                  <div className="dov-track" style={{ width: trackWidth }}>
                    {hourTicks(axis).map((tick) => (
                      <span key={tick.at} className="dov-hour" style={{ left: tick.x }}>{tick.label}</span>
                    ))}
                  </div>
                </div>

                {lanes.map((lane) => {
                  const bars = lane.placements.map((placement) => {
                    const leg = legOf(placement.trip);
                    return { ...placement, leg, ...barGeometry(leg, axis) };
                  });
                  const rows = packRows(bars);
                  const mine = new Set(lane.placements.map(({ trip }) => trip));
                  const bands = overlaps.filter(({ a, b }) => mine.has(a) || mine.has(b));

                  return (
                    <div
                      key={lane.key}
                      className={`dov-row${lane.unassigned ? ' dov-row--unassigned' : ''}`}
                      style={{ height: (Math.max(...rows) + 1) * ROW_PX + 6 }}
                    >
                      <div className="dov-label">
                        <span className="dov-label__name">
                          <span>{lane.name}</span>
                          {bands.length > 0 && <span className="dov-label__flag">Overlap</span>}
                        </span>
                        <span className="dov-label__sub">{laneSummary(lane)}</span>
                      </div>
                      <div className="dov-track" style={{ width: trackWidth }}>
                        {bands.map(({ driverId, from, to }) => (
                          <span
                            key={`${driverId}-${from}`}
                            className="dov-band"
                            aria-hidden="true"
                            style={{
                              left: xOf(axis, from),
                              width: Math.max(4, xOf(axis, to) - xOf(axis, from)),
                            }}
                          />
                        ))}
                        {bars.map((bar, i) => {
                          const { trip } = bar;
                          const { isReturn } = bar.leg;
                          const { unassigned } = lane;
                          const look = barLook(trip, now, { unassigned, isReturn });
                          const alert = ALERT_LOOKS.includes(barLook(trip, now, { isReturn }));
                          const icon = iconOf(trip, alert);
                          const wide = bar.width >= WIDE_BAR_PX;
                          const top = 3 + rows[i] * ROW_PX;
                          const sentence = sentenceOf(bar, look, overlapping.has(trip));
                          const isSelected = trip === selected;

                          return (
                            <React.Fragment key={idOf(trip)}>
                              {bar.tail > 0 && (
                                <span
                                  className="dov-tail"
                                  aria-hidden="true"
                                  style={{ left: bar.left + bar.width, width: bar.tail, top }}
                                />
                              )}
                              <button
                                type="button"
                                className={[
                                  'dov-trip',
                                  `dov-trip--${look}`,
                                  bar.estimated ? '' : 'dov-trip--open-end',
                                  isSelected ? 'is-selected' : '',
                                  faded(trip) ? 'is-faded' : '',
                                ].filter(Boolean).join(' ')}
                                style={{ left: bar.left, width: bar.width, top }}
                                title={sentence}
                                aria-label={sentence}
                                aria-pressed={isSelected}
                                onClick={() => select(trip)}
                              >
                                <span className="dov-trip__line">
                                  {icon && <i className={`fa ${icon}`} aria-hidden="true" />}
                                  <span className="dov-trip__time">{tripClock(trip, bar.leg.start)}</span>
                                  {wide && <span className="dov-trip__who">{whoOf(trip)}</span>}
                                </span>
                                <span className="dov-trip__line dov-trip__line--sub">
                                  {wide ? [routeOf(trip), driverOf(bar)].filter(Boolean).join(' · ') : whoOf(trip)}
                                </span>
                              </button>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {showsNow && (
                  <div className="dov-overlay">
                    <div className="dov-past" aria-hidden="true" style={{ width: nowX }} />
                    <div className="dov-now-at" style={{ left: nowX }}>
                      <NowLine
                        block={NOW_LINE_DASH}
                        label={NOW_LABEL_EN}
                        time={nowTimeLabel(now)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {untimed.length > 0 && (
            <div className="dov-untimed">
              <span className="dov-untimed__title">No time set</span>
              {untimed.map((trip) => (
                <button
                  key={idOf(trip)}
                  type="button"
                  className={`dov-untimed__trip${faded(trip) ? ' is-faded' : ''}${trip === selected ? ' is-selected' : ''}`}
                  aria-pressed={trip === selected}
                  onClick={() => select(trip)}
                >
                  <strong>{whoOf(trip)}</strong>
                  {routeOf(trip) ? ` · ${routeOf(trip)}` : ''}
                </button>
              ))}
            </div>
          )}
        </section>

        {selected && (
          <TripPanel
            trip={selected}
            leg={legOf(selected)}
            now={now}
            overlaps={overlaps.filter(({ a, b }) => a === selected || b === selected)}
            onClose={() => setSelectedId(null)}
            onEditBooking={onEditBooking}
            onAssignOperator={onAssignOperator}
            onAssignDriver={onAssignDriver}
            onViewDetails={onViewDetails}
            onViewDriverEvents={onViewDriverEvents}
            onSearchLinkedBookings={onSearchLinkedBookings}
          />
        )}
      </div>
    </div>
  );
}

export default DayTimeline;
